import {useCallback} from 'react'
import {View} from 'react-native'
import {Image} from 'expo-image'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {BACK_HITSLOP} from '#/lib/constants'
import {type NavigationProp} from '#/lib/routes/types'
import {atoms as a, platform} from '#/alf'
import {Button} from '#/components/Button'
import {ArrowLeft_Stroke2_Corner0_Rounded as ArrowLeftIcon} from '#/components/icons/Arrow'
import {LinearGradientBackground} from '#/components/LinearGradientBackground'

/**
 * A classroom's cover.
 *
 * The same 3:1 band a profile uses, and deliberately nothing else: a classroom
 * has no face, so there is no avatar sitting on the corner of it the way a
 * profile's does. The name and the counts live below the band instead.
 */
export function ClassroomBanner({
  cover,
  testID = 'classroomBanner',
  showBackButton = true,
}: {
  cover?: string
  testID?: string
  showBackButton?: boolean
}) {
  const {t: l} = useLingui()
  const {top} = useSafeAreaInsets()
  const navigation = useNavigation<NavigationProp>()

  const onPressBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.navigate('Home')
    }
  }, [navigation])

  return (
    <View style={[a.relative, {height: 150}]}>
      {cover ? (
        <Image
          testID={`${testID}Image`}
          source={{uri: cover}}
          style={[a.w_full, {height: 150}]}
          contentFit="cover"
          accessibilityIgnoresInvertColors
        />
      ) : (
        <LinearGradientBackground gradient="sky" style={[a.w_full, a.h_full]} />
      )}
      {showBackButton && (
        <Button
          testID="classroomHeaderBackBtn"
          onPress={onPressBack}
          hitSlop={BACK_HITSLOP}
          label={l`Back`}
          style={[
            a.absolute,
            a.pointer,
            {
              top: platform({web: 10, default: Math.max(top, 12)}),
              left: platform({web: 18, default: 12}),
            },
          ]}>
          {({hovered}) => (
            <View
              style={[
                a.align_center,
                a.justify_center,
                a.rounded_full,
                {
                  width: 31,
                  height: 31,
                  backgroundColor: hovered
                    ? 'rgba(0, 0, 0, 0.75)'
                    : 'rgba(0, 0, 0, 0.5)',
                },
              ]}>
              <ArrowLeftIcon size="lg" fill="white" />
            </View>
          )}
        </Button>
      )}
    </View>
  )
}
